import Constants from '@intercom/embercom-composer/lib/constants';
import { classForType } from '@intercom/embercom-composer/blocks/types/base/block';
import BlockPoint from './block-point';
import BlockRange from './block-range';
import { joinBy, subtractFrom } from '@intercom/pulse/lib/computed-properties';
// TODO - encoding lib
import stripAttrs from '@intercom/embercom-composer/lib/strip-data-attributes';
import ValidatedArraySet from './mixins/validated-array-set';
import { hasNativeEmojiSupport, replaceUnicodeWithColonified } from '@intercom/embercom-composer/lib/intermoji';
import AttributeReplacement from '@intercom/embercom-composer/blocks/replacements/attribute';
import renderPreview from '@intercom/embercom-composer/lib/render-preview';
import { filterBy, notEmpty, equal, not, reads } from '@ember/object/computed';
import Em from 'ember';
const UPLOADABLE_BLOCKS = ['image', 'messengerCard', 'videoFile'];
export default Em.ArrayProxy.extend(ValidatedArraySet, {
  defaultBlockType: null,
  allowedChildBlocks: ['button', 'code', 'facebookLikeButton', 'heading', 'html', 'image', 'videoFile', 'link', 'orderedList', 'paragraph', 'subheading', 'twitterFollowButton', 'unorderedList', 'video', 'messengerCard', 'videoFile', 'imageText'],
  validate(block) {
    return !Em.isNone(block) && this.allowedChildBlocks.includes(block.get('type'));
  },
  imageLoadErrors: filterBy('content', 'hasUploadError'),
  hasMarkers: notEmpty('blocksWithMarkers'),
  blocksWithMarkers: filterBy('content', 'hasMarkers'),
  setup: Em.on('init', function () {
    if (!this.get('config')) {
      throw new Error('Blocklist created without config');
    }
    this.set('content', Em.A());
    if (!this.get('defaultBlockType')) {
      this.set('defaultBlockType', classForType('paragraph'));
    }
  }),
  html: joinBy('content', 'html', ''),
  serialized: Em.computed('content.@each.serialized', 'content.[]', 'serializedWithEmptyTrailingBlocks', function () {
    if (this.get('config.stripEmptyTrailingBlocks')) {
      return this._serializedWithoutEmptyTrailingBlocks();
    } else {
      return this.get('serializedWithEmptyTrailingBlocks');
    }
  }),
  serializedWithEmptyTrailingBlocks: Em.computed('content.@each.serialized', 'content.[]', function () {
    return this.get('content').mapBy('serialized').compact();
  }),
  _serializedWithoutEmptyTrailingBlocks() {
    let content = this.get('content');
    let block;
    let hasFoundNonEmptyBlock = false;
    let serialized = [];
    for (let i = content.get('length') - 1; i >= 0; i--) {
      block = content[i];
      block.get('serialized'); //fetch now to ensure the property is up to date
      if (!hasFoundNonEmptyBlock) {
        hasFoundNonEmptyBlock = !(block.get('isParagraph') && block.get('isEmpty'));
      }
      if (hasFoundNonEmptyBlock || i === 0) {
        serialized.unshift(block.get('serialized'));
      }
    }
    return serialized.compact();
  },
  htmlWithoutIDs: Em.computed('html', function () {
    return stripAttrs(this.get('html'));
  }),
  htmlWithPreview: Em.computed('html', 'config.previewContext', function () {
    let context = this.get('config.previewContext');
    if (!context) {
      context = {};
    }
    return renderPreview(this.get('html'), context);
  }),
  hasNoContent: equal('htmlWithoutIDs', '<p data-align="left"></p>'),
  hasContent: not('hasNoContent'),
  endBlockIndex: subtractFrom('content.length', 1),
  endItemIndex: reads('lastObject.endItemIndex'),
  endPositionIndex: reads('lastObject.endPositionIndex'),
  entityInfo: Em.computed('content.@each.html', function () {
    let blockIndex = 0;
    return this.get('content').map(function (block) {
      blockIndex++;
      return `${blockIndex}:${block.get('entities.info')}`;
    }).join(Constants.NEW_LINE);
  }),
  secondLastObject: Em.computed('content.[]', function () {
    if (this.get('length') > 1) {
      return this.objectAt(this.get('length') - 2);
    }
    return null;
  }),
  lastUpdated: Em.computed('serialized', function () {
    return new Date().getTime();
  }),
  deleteEntityById(id) {
    // TODO - This should be handled by a command
    this.get('content').forEach(function (block) {
      block.deleteEntityById(id);
    });
  },
  restoreCheckpoint(data, uploadManager) {
    let checkpointBlockList = this.constructor.deserialize(JSON.parse(data), this.get('config'), uploadManager);
    this.clear();
    this.pushObjects(checkpointBlockList.toArray());
  },
  selectionContainsEntityType(selection, entityType) {
    let selectedBlocks = selection.get('selectedBlocks');
    let i;
    let block;
    for (i = selectedBlocks.get('length') - 1; i >= 0; i--) {
      block = selectedBlocks.objectAt(i);
      if (this._isEntityTypeInBlockSelection(entityType, block, selection)) {
        return true;
      }
    }
    return false;
  },
  selectionCoveredByEntityType(selection, entityType) {
    let selectedBlocks = selection.get('selectedBlocks');
    let i;
    let block;
    for (i = selectedBlocks.get('length') - 1; i >= 0; i--) {
      block = selectedBlocks.objectAt(i);
      if (!this._isEntityTypeInBlockSelection(entityType, block, selection)) {
        return false;
      }
    }
    return true;
  },
  insertBlocksAtSelection(selection, blocks) {
    //TODO: This should be moved into a command
    let mutationResult = Em.Object.create();
    let caretIndex = selection.get('startBlockIndex');
    let firstSelectedBlock = selection.get('firstSelectedBlock');
    let blockCount = blocks.get('length');
    let i = 0;
    blocks.forEach(function (block) {
      let joinResult;
      if (i === 0 && block.get('type') === 'paragraph') {
        joinResult = firstSelectedBlock.join(block);
      }
      if (!joinResult || !joinResult.get('blocksToRemove').includes(block)) {
        this.insertAt(caretIndex, block);
      }
      if (i < blockCount - 1) {
        caretIndex++;
      }
      i++;
    }, this);
    mutationResult.set('newCaretIndex', caretIndex);
    mutationResult.set('updatesCaret', true);
    let caretBlock = this.objectAt(caretIndex);
    mutationResult.set('newStartPoint', BlockPoint.create({
      itemIndex: 0,
      positionIndex: caretBlock.get('text.length')
    }));
    return mutationResult;
  },
  _isEntityTypeInBlockSelection(entityType, block, selection) {
    let blockRange = this._blockRangeForSelection(block, selection);
    return block.hasEntityTypeInRange(entityType, blockRange);
  },
  _blockRangeForSelection(block, selection) {
    let startItemIndex, startPositionIndex, endItemIndex, endPositionIndex;
    startItemIndex = 0;
    startPositionIndex = 0;
    endPositionIndex = block.get('text.length') || block.get('blockList.lastObject.text.length') || 0;
    endItemIndex = block.get('blockList.length') || 0;
    if (block === selection.get('firstSelectedBlock')) {
      startItemIndex = selection.get('startItemIndex');
      startPositionIndex = selection.get('startPositionIndex');
    }
    if (block === selection.get('lastSelectedBlock')) {
      endItemIndex = selection.get('endItemIndex');
      endPositionIndex = selection.get('endPositionIndex');
    }
    return BlockRange.atItemAndPositionIndex(startItemIndex, startPositionIndex, endItemIndex, endPositionIndex);
  },
  _ensureDefaultBlock() {
    let blockList = this.get('content');
    if (blockList.get('length') === 0) {
      blockList.pushObject(this.get('defaultBlockType').create({
        config: this.get('config')
      }));
    }
  },
  insertDefaultBlockAt(index) {
    let newBlock = this.get('defaultBlockType').create({
      config: this.get('config')
    });
    this.get('content').insertAt(index, newBlock);
    return newBlock;
  },
  _removeBlocks(blocks) {
    this.removeObjects(blocks);
  }
}).reopenClass({
  deserialize(blocks, config, uploadManager) {
    let blockList = this.create({
      config
    });
    let blockInstance;
    blocks = blocks || [];
    let index = 0;
    blocks.forEach(block => {
      block = Object.assign({}, block);
      if (block.type === 'attachmentList') {
        return;
      }
      if (block.text) {
        block.text = block.text.replace(new RegExp(Constants.ZERO_WIDTH_SPACE, 'g'), '');
      }
      if (block.text && config.get('allowEmoji') && !hasNativeEmojiSupport()) {
        block.text = replaceUnicodeWithColonified(block.text);
      }
      block.config = config;
      if (UPLOADABLE_BLOCKS.includes(block.type)) {
        block.uploadManager = uploadManager;
      }
      blockInstance = classForType(block.type).create(block);
      if (!block.align) {
        this._applyAutoCentering(config, blockInstance, block, index);
      }
      if (blockInstance.get('isTextType')) {
        AttributeReplacement.tryReplacement(blockInstance);
      }
      blockList.pushObject(blockInstance);
      index++;
    });
    return this.stripNoMarginParagraphs(blockList, config);
  },
  _applyAutoCentering(config, blockInstance, block, index) {
    let autoCenter = config.get('autoCenter');
    if (config.get('allowAlignment') && autoCenter && blockInstance.get('canAlign')) {
      if (autoCenter.includes('initialHeading') && index === 0 && blockInstance.get('isHeadingType')) {
        blockInstance.set('align', 'center');
      }
      if (autoCenter.includes('image') && blockInstance.get('isImage')) {
        blockInstance.set('align', 'center');
      }
      if (config.autoCenter.includes('button') && blockInstance.get('isButton')) {
        blockInstance.set('align', 'center');
      }
    }
  },
  stripNoMarginParagraphs(blockList, config) {
    let newBlockList = this.create({
      config
    });
    let activeParagraphBlock;
    blockList.forEach(block => {
      if (block.type === 'paragraph' && block.class === 'no-margin') {
        if (!activeParagraphBlock) {
          newBlockList.pushObject(block);
          activeParagraphBlock = block;
        } else if (block.text === '') {
          activeParagraphBlock = undefined;
        } else {
          activeParagraphBlock.text += `\n${block.text}`;
        }
      } else {
        activeParagraphBlock = undefined;
        newBlockList.pushObject(block);
      }
    });
    return newBlockList;
  }
});